import {Result, ok, err} from "neverthrow"

export type ResponseType = "text" | "json" | "blob" | "arraybuffer"

export interface BasicAuthCredentials {
	username: string
	password: string
}

export interface RequestConfig {
	headers?: Record<string, string | undefined>
	responseType?: ResponseType
	withCredentials?: boolean
	data?: any
	/** HTTP Basic Auth (axios-compatible; only username/password are sent). */
	auth?: BasicAuthCredentials & Record<string, unknown>
}

export interface HttpResponse<T = any> {
	data: T
	status: number
	statusText: string
	headers: Record<string, string>
	config: RequestConfig
}

class HttpError extends Error {
	response: HttpResponse

	constructor(response: HttpResponse) {
		super(`Request failed with status ${response.status}`)
		this.response = response
	}
}

function isHttpError(error: unknown): error is HttpError {
	return error instanceof HttpError
}

/**
 * Shape-compatible with `ErrorDocument` from resources/common. Defined locally to
 * avoid a circular import. On non-HTTP failures (e.g. `fetch` throwing
 * `TypeError: Failed to fetch`), fetch returns a raw `Error` whereas most
 * consumers assume an `{errors: [...]}` document and read `err.errors[0]`.
 * Normalizing here protects every call site from crashing on network failures.
 */
function networkErrorDocument(error: unknown): {errors: Array<{title: string; status: string; detail?: string}>} {
	const detail = error instanceof Error ? error.message : String(error)
	return {errors: [{title: "Network Error", status: "0", detail}]}
}

function normalizeError<E>(error: unknown): E {
	if (isHttpError(error)) return error.response.data as E
	return networkErrorDocument(error) as E
}

function serializeBody(data: any): BodyInit | undefined {
	if (data === undefined || data === null) return undefined
	if (typeof data === "string") return data
	if (data instanceof Blob || data instanceof ArrayBuffer || data instanceof FormData) return data
	return JSON.stringify(data)
}

function headersToRecord(headers: Headers): Record<string, string> {
	const record: Record<string, string> = {}
	headers.forEach((value, key) => {
		record[key.toLowerCase()] = value
	})
	return record
}

async function parseResponseData(response: Response, responseType?: ResponseType): Promise<any> {
	if (response.status === 204) return undefined

	const type = responseType || "json"
	switch (type) {
		case "text":
			return response.text()
		case "blob":
			return response.blob()
		case "arraybuffer":
			return response.arrayBuffer()
		case "json":
		default: {
			const text = await response.text()
			if (!text) return undefined
			try {
				return JSON.parse(text)
			} catch {
				return text
			}
		}
	}
}

function normalizeHeaders(headers?: Record<string, string | undefined>): Record<string, string> | undefined {
	if (!headers) return undefined
	const normalized: Record<string, string> = {}
	for (const [key, value] of Object.entries(headers)) {
		if (value !== undefined) normalized[key] = value
	}
	return normalized
}

function basicAuthorizationHeader(username: string, password: string): string {
	const credentials = `${username}:${password}`
	const bytes = new TextEncoder().encode(credentials)
	const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), "")
	return `Basic ${btoa(binary)}`
}

function headersWithAuth(
	headers?: Record<string, string | undefined>,
	auth?: BasicAuthCredentials & Record<string, unknown>
): Record<string, string> | undefined {
	if (!auth) return normalizeHeaders(headers)

	const normalized = normalizeHeaders(headers) ?? {}
	const hasAuthorization = Object.keys(normalized).some((key) => key.toLowerCase() === "authorization")
	if (hasAuthorization) return normalized

	return {
		...normalized,
		Authorization: basicAuthorizationHeader(auth.username, auth.password),
	}
}

/** Dispatched on `window` when an authenticated request is rejected with 401 (invalid/expired session). */
export const UNAUTHORIZED_EVENT = "unit:unauthorized"

/**
 * Login / MFA endpoints legitimately return 401 (e.g. wrong password or code) and must not end the session.
 * Note: `/token/v2` (claims) and `DELETE /token` (logout) are authenticated and intentionally NOT excluded.
 */
const LOGIN_ENDPOINT = /\/token(\/(checkVerification|createVerification|webauthn\/.*))?\/?$/

function isTokenEndpoint(url: string, method: string): boolean {
	try {
		const {pathname} = new URL(url, window.location.origin)
		if (/\/token\/?$/.test(pathname)) return method === "POST"
		return LOGIN_ENDPOINT.test(pathname)
	} catch {
		return false
	}
}

function notifyUnauthorized(method: string, url: string, status: number) {
	if (status !== 401 || isTokenEndpoint(url, method)) return
	window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
}

async function request<T>(method: string, url: string, data: any, config: RequestConfig): Promise<HttpResponse<T>> {
	const body = method === "GET" || method === "DELETE" ? undefined : serializeBody(data)

	const response = await fetch(url, {
		method,
		headers: headersWithAuth(config.headers, config.auth),
		body,
		credentials: config.withCredentials === false ? "omit" : "include",
	})

	const parsedData = await parseResponseData(response, config.responseType)
	const httpResponse: HttpResponse<T> = {
		data: parsedData as T,
		status: response.status,
		statusText: response.statusText,
		headers: headersToRecord(response.headers),
		config,
	}

	if (!response.ok) {
		notifyUnauthorized(method, url, response.status)
		throw new HttpError(httpResponse)
	}

	return httpResponse
}

async function handleGetError<E>(error: unknown): Promise<Result<any, E>> {
	if (isHttpError(error)) {
		const response = error.response
		if (response.config.responseType == "blob" && response.data instanceof Blob)
			return err(JSON.parse(await response.data.text()))
		return err(response.data)
	}
	return err(networkErrorDocument(error) as E)
}

export async function post<T, E>(url: string, data: any, config: RequestConfig): Promise<Result<T, E>> {
	try {
		const response = await request<T>("POST", url, data, config)
		return ok(response.data)
	} catch (error) {
		return err(normalizeError<E>(error))
	}
}

export async function getResponse<E>(url: string, config: RequestConfig): Promise<Result<any, E>> {
	try {
		const response = await request<any>("GET", url, undefined, config)
		return ok(response)
	} catch (error) {
		return handleGetError(error)
	}
}

export async function get<T, E>(url: string, config: RequestConfig): Promise<Result<T, E>> {
	try {
		const response = await request<T>("GET", url, undefined, config)
		return ok(response.data)
	} catch (error) {
		return handleGetError(error)
	}
}

export async function patch<T, E>(url: string, data: any, config: RequestConfig): Promise<Result<T, E>> {
	try {
		const response = await request<T>("PATCH", url, data, config)
		return ok(response.data)
	} catch (error) {
		return err(normalizeError<E>(error))
	}
}

export async function deleteVerb<T, E>(url: string, config: RequestConfig): Promise<Result<T, E>> {
	try {
		const response = await request<T>("DELETE", url, undefined, config)
		return ok(response.data)
	} catch (error) {
		return err(normalizeError<E>(error))
	}
}

export async function put<T, E>(url: string, data: any, config: RequestConfig): Promise<Result<T, E>> {
	try {
		const response = await request<T>("PUT", url, data, config)
		return ok(response.data)
	} catch (error) {
		return err(normalizeError<E>(error))
	}
}

export async function rawPost<T, E>(
	url: string,
	data: any,
	config: RequestConfig
): Promise<Result<HttpResponse<T>, HttpResponse<E>>> {
	try {
		const response = await request<T>("POST", url, data, config)
		return ok(response)
	} catch (error) {
		if (isHttpError(error)) return err(error.response as HttpResponse<E>)
		return err({
			data: networkErrorDocument(error) as E,
			status: 0,
			statusText: "Network Error",
			headers: {},
			config,
		})
	}
}
